import colors from "tailwindcss/colors.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useMemo } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const CAMPAIGN_STATUS = {
  SCHEDULED: "Scheduled",
  COMPLETED: "Completed",
  // ONGOING: "Ongoing",
  FAILED: "Failed",
  UNANSWERED: "Unanswered",
  VOICEMAIL: "Voicemail",
  BLOCKED: "Blocked",
  DELIVERED: "Delivered",
  REJECTED: "Rejected",
  QUEUED: "Queued",
  CallPutOnHold: "CallPutOnHold",
};

const StatusColors = {
  [CAMPAIGN_STATUS.COMPLETED]: colors.green[500],
  [CAMPAIGN_STATUS.SCHEDULED]: colors.amber[500],
  [CAMPAIGN_STATUS.DELIVERED]: colors.green[400],
  [CAMPAIGN_STATUS.VOICEMAIL]: colors.violet[500],
  // [CAMPAIGN_STATUS.ONGOING]: colors.yellow[500],
  [CAMPAIGN_STATUS.SCHEDULED]: colors.amber[500],
  [CAMPAIGN_STATUS.UNANSWERED]: colors.pink[500],
  [CAMPAIGN_STATUS.FAILED]: colors.red[500],
  [CAMPAIGN_STATUS.BLOCKED]: colors.red[500],
  [CAMPAIGN_STATUS.REJECTED]: colors.red[500],
  [CAMPAIGN_STATUS.QUEUED]: colors.orange[500],
  [CAMPAIGN_STATUS.CallPutOnHold]: colors.blue[500],
};

const plugins = {
  legend: {
    display: false,
  },
  onClick: () => {},
};

const scale = {
  grid: {
    display: false,
  },
  ticks: {
    font: {
      size: 14,
      weight: "semibold",
      lineHeight: 1,
    },
  },
  border: {
    width: 0,
  },
};

const options = {
  aspectRatio: 1.4,
  responsive: true,
  indexAxis: "y",
  plugins,
  scales: {
    y: scale,
    x: scale,
  },
};

export default function OutboundStatus(props) {
  const { outboundByStatus, isLoading } = props;

  const { chartData, backgroundColors, callCount } = useMemo(
    function () {
      const status = Object.values(CAMPAIGN_STATUS);

      const chartData = status.reduce(
        function (acc, _status) {
          const _data = outboundByStatus[_status] ?? 0;
          const _color = StatusColors[_status] ?? colors.gray[400];

          acc.chartData.push(_data);
          acc.backgroundColors.push(_color);
          acc.callCount += _data;

          return acc;
        },
        { chartData: [], backgroundColors: [], callCount: 0 }
      );

      return chartData;
    },
    [outboundByStatus]
  );

  const data = useMemo(
    function () {
      return {
        labels: Object.values(CAMPAIGN_STATUS),
        datasets: [
          {
            axis: "x",
            data: chartData,
            label: "Status",
            backgroundColor: backgroundColors,
          },
        ],
      };
    },
    [chartData, backgroundColors]
  );

  const Component = useMemo(
    function () {
      if (isLoading) {
        return (
          <div className="flex items-center justify-center h-full min-h-96">
            <AiOutlineLoading3Quarters className="animate-spin" />
          </div>
        );
      }

      if (callCount > 0) {
        return (
          <Bar
            options={options}
            data={data}
          />
        );
      }

      return (
        <div className="flex items-center justify-center min-h-96 gap-x-2 text-gray-500">
          <InformationCircleIcon className="size-4" />
          <p className="text-sm">No data</p>
        </div>
      );
    },
    [isLoading, data, callCount]
  );

  return (
    <>
      <p className="text-md font-semibold">Outbound Call Statistics</p>
      {Component}
    </>
  );
}
